import { Component, Injector, Input } from '@angular/core';
import type { OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavParams, IonicModule } from '@ionic/angular';

import type { TAddressModalViewModel, TLocation, TValidation } from '@shared/models';
import { AddressVerificationService, ServiceAreasService } from '@shared/services';
import { AddressBasePage } from '../addressBase/addressBase.page';

@Component({
    selector: 'app-address-modal',
    templateUrl: './addressModal.page.html',
    styleUrls: ['./addressModal.page.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule, IonicModule]
})
export class AddressModalPage extends AddressBasePage implements OnInit {
    // public amvm: TAddressModalViewModel = {
    //     'title': '',
    //     'optionLabel1': '',
    //     'optionLabel2': '',
    //     'dealershipLocation': {
    //         'locationName': '',
    //         'address': {
    //             'streetAddress': '',
    //             'city': '',
    //             'regionName': '',
    //             'zipcode': '',
    //             'coordinate': { 'latitude': 0, 'longitude': 0 },
    //             'countryAbbr': '',
    //             'countryName': '',
    //             'fullAddress': '',
    //         }
    //     },
    //     'customStartingLocation': {
    //         'locationName': '',
    //         'address': {
    //             'streetAddress': '',
    //             'city': '',
    //             'regionName': '',
    //             'zipcode': '',
    //             'coordinate': { 'latitude': 0, 'longitude': 0 },
    //             'countryAbbr': '',
    //             'countryName': '',
    //             'fullAddress': '',
    //         }
    //     },
    //     'position': '',
    //     'addressVerified': false,
    //     'startingSource': 'dealership',
    //     'endingSource': 'dealership',
    // };
    public addressVerified = false;
    public addressDirty = false;
    public amvm: TAddressModalViewModel;

    // ================================================
    // Lifecycle Methods
    // ================================================
    constructor(private navParams: NavParams, private addressVerificationService: AddressVerificationService,
        private serviceAreasService: ServiceAreasService, injector: Injector) {
        super(injector);
    }

    public override ngOnInit(): void {
        super.ngOnInit();
        this.amvm = this.navParams.get('addressModalViewModel');

        // Set initial verification state
        this.addressVerified = this.getCurrentSource() === 'dealership';
    }

    // ================================================
    // Simplified Helper Methods
    // ================================================

    /**
     * Get the current source based on position
     */
    private getCurrentSource(): 'dealership' | 'customLocation' {
        return this.amvm.position === 'start'
            ? this.amvm.startingSource
            : this.amvm.endingSource;
    }

    /**
     * Set the current source based on position
     */
    private setCurrentSource(value: 'dealership' | 'customLocation'): void {
        if (this.amvm.position === 'start') {
            this.amvm.startingSource = value;
        } else {
            this.amvm.endingSource = value;
        }
    }

    /**
     * Get the current location object based on position
     */
    private getCurrentLocation(): TLocation {
        const location = this.amvm.position === 'start'
            ? this.amvm.startingLocation
            : this.amvm.endingLocation;
        return location;
    }

    // ================================================
    // Source Management
    // ================================================

    public isSourceDealership(): boolean {
        return this.getCurrentSource() === 'dealership';
    }

    public onSourceChange(): void {
        const newSource = this.getCurrentSource();

        if (newSource === 'dealership') {
            // User switched TO dealership - restore dealership values
            if (this.amvm.position === 'start') {
                this.amvm.startingLocation = this.cloneLocation(this.amvm.dealershipLocation);
            } else {
                this.amvm.endingLocation = this.cloneLocation(this.amvm.dealershipLocation);
            }
            this.addressVerified = true;
            this.addressDirty = false;
        } else {
            // User switched TO custom - location already has values
            // (either from previous custom entry or from dealership default)
            this.addressVerified = false;
            this.addressDirty = false;
        }
    }

    // ================================================
    // Location Getters/Setters (Simplified!)
    // ================================================

    public getLocationName(): string {
        return this.getCurrentLocation().locationName;
    }

    public setLocationName(event: any): void {
        this.getCurrentLocation().locationName = event.detail.value;
    }

    public getStreetAddress(): string {
        return this.getCurrentLocation().address.streetAddress;
    }

    public setStreetAddress(event: any): void {
        this.getCurrentLocation().address.streetAddress = event.detail.value;
        this.addressDirty = true;
    }

    public getCityName(): string {
        return this.getCurrentLocation().address.city;
    }

    public setCityName(event: any): void {
        this.getCurrentLocation().address.city = event.detail.value;
        this.addressDirty = true;
    }

    public getZipcode(): string {
        return this.getCurrentLocation().address.zipcode;
    }

    public setZipcode(event: any): void {
        this.getCurrentLocation().address.zipcode = event.detail.value;
        this.addressDirty = true;
    }

    public getStateName(): string {
        return this.getCurrentLocation().address.regionName;
    }

    public setStateName(event: any): void {
        this.getCurrentLocation().address.regionName = event.detail.value;
        this.addressDirty = true;
    }

    // ================================================
    // Address Verification (Simplified!)
    // ================================================

    public async onVerifyAddressClick(): Promise<void> {
        try {
            this.addressVerified = false;
            await this.uxHelperService.showSpinner('Verifying address...');

            const currentLocation = this.getCurrentLocation();
            const validation: TValidation = await this.addressVerificationService
                .verifyAddress(currentLocation.address);

            if (validation.found) {
                this.addressDirty = false;
                this.addressVerified = true;
                currentLocation.address.streetAddress = validation.foundAddress;
                currentLocation.address.fullAddress = validation.location.formattedAddress;
                currentLocation.address.city = validation.foundCity;
                currentLocation.address.coordinate = validation.location.coordinate;
            }

            await this.uxHelperService.hideSpinner();
            await this.potentiallyUseFoundInfo(validation, currentLocation.address);
        } catch (err) {
            await this.uxHelperService.hideSpinner();
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Address Not Found', errorMessage);
        }
    }

    // ================================================
    // Button Actions
    // ================================================

    public canConfirm(): boolean {
        return this.addressVerified;
    }

    public async onCancelClick(): Promise<void> {
        await this.modalController.dismiss({
            okay: false,
            addressModalViewModel: this.amvm
        });
    }

    public async onOkayClick(): Promise<void> {
        await this.modalController.dismiss({
            okay: true,
            addressModalViewModel: this.amvm
        });
    }

    private cloneLocation(location: TLocation): TLocation {
        return JSON.parse(JSON.stringify(location));
    }
}